import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { useAuth } from '../../services/cognito';
import config from '../../config';
import { Layer } from '../../types';

// What the admin is about to reset — drives the confirm dialog.
type ResetTarget =
  | { kind: 'global' }
  | { kind: 'territory'; layer1: string }
  | { kind: 'card'; layer1: string; layer2: string };

const GLOBAL_CONFIRM_WORD = '초기화';

const TerritoryReset: React.FC = () => {
  const { token } = useAuth();
  const [territories, setTerritories] = useState<Layer[]>([]);
  const [sections, setSections] = useState<Record<string, Layer[]>>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState<ResetTarget | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const authHeader = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined;

  const fetchTerritories = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${config.api.url}/mdsouth/layers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTerritories(data.layers ?? []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: '구역 목록을 불러오지 못했습니다',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTerritories();
  }, [fetchTerritories]);

  const loadSections = async (layer1: string) => {
    if (sections[layer1]) return; // already loaded
    try {
      const { data } = await axios.get(
        `${config.api.url}/mdsouth/layers/${layer1}`,
      );
      setSections(prev => ({ ...prev, [layer1]: data.layers ?? [] }));
    } catch (err) {
      setSnackbar({
        open: true,
        message: `${layer1}번 구역의 카드 목록을 불러오지 못했습니다`,
        severity: 'error',
      });
    }
  };

  const closeDialog = () => {
    setTarget(null);
    setConfirmText('');
  };

  const runReset = async () => {
    if (!target) return;
    const url =
      target.kind === 'global'
        ? `${config.api.url}/mdsouth/reset`
        : target.kind === 'territory'
        ? `${config.api.url}/mdsouth/reset/${target.layer1}`
        : `${config.api.url}/mdsouth/reset/${target.layer1}/${target.layer2}`;
    setBusy(true);
    try {
      const { data } = await axios.post(url, {}, authHeader);
      setSnackbar({
        open: true,
        message: `초기화 완료 — 카드 ${data.cards ?? 1}개, 세대 ${data.householdsReset}건, 방문자기록 ${data.visitorRecordsDeleted}건 (방문금지 유지)`,
        severity: 'success',
      });
      closeDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: '초기화에 실패했습니다',
        severity: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const confirmDisabled =
    busy ||
    (target?.kind === 'global' && confirmText.trim() !== GLOBAL_CONFIRM_WORD);

  const dialogText = (): string => {
    if (!target) return '';
    if (target.kind === 'global')
      return '전체 구역의 모든 카드를 초기화합니다. 되돌릴 수 없습니다.';
    if (target.kind === 'territory')
      return `${target.layer1}번 구역의 모든 카드를 초기화합니다.`;
    return `${target.layer1}-${target.layer2} 카드를 초기화합니다.`;
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        구역 리셋 관리
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        방문 상태(미방문/재방필요/만났음)와 방문자 기록을 초기화합니다.
        <strong> 방문금지로 표시된 세대는 유지됩니다.</strong>
      </Typography>

      <Button
        variant="contained"
        color="error"
        startIcon={<WarningAmberIcon />}
        sx={{ mb: 2 }}
        onClick={() => setTarget({ kind: 'global' })}
      >
        전체 구역 초기화
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        territories.map(t => (
          <Accordion
            key={t.id}
            disableGutters
            variant="outlined"
            onChange={(_e, expanded) => expanded && loadSections(t.name)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ width: '100%', pr: 2 }}
              >
                <Typography sx={{ flexGrow: 1 }}>
                  {t.name}번 구역
                  {t.description ? ` — ${t.description}` : ''}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<RestartAltIcon />}
                  onClick={e => {
                    e.stopPropagation();
                    setTarget({ kind: 'territory', layer1: t.name });
                  }}
                >
                  구역 전체 리셋
                </Button>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {sections[t.name] ? (
                sections[t.name].length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {sections[t.name].map(s => (
                      <Chip
                        key={s.id}
                        label={`${s.name} 리셋`}
                        color="error"
                        variant="outlined"
                        onClick={() =>
                          setTarget({
                            kind: 'card',
                            layer1: t.name,
                            layer2: s.name,
                          })
                        }
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    카드가 없습니다
                  </Typography>
                )
              ) : (
                <CircularProgress size={20} />
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* 초기화 확인 다이얼로그 */}
      <Dialog open={!!target} onClose={busy ? undefined : closeDialog}>
        <DialogTitle>
          {target?.kind === 'global' ? '전체 구역 초기화' : '구역 초기화'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {dialogText()}
            <br />
            방문금지 세대는 그대로 유지됩니다.
            {target?.kind === 'global' && (
              <Box sx={{ mt: 2 }}>
                계속하려면 <strong>{GLOBAL_CONFIRM_WORD}</strong> 를
                입력하세요.
                <TextField
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={GLOBAL_CONFIRM_WORD}
                />
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>
            취소
          </Button>
          <Button
            onClick={runReset}
            color="error"
            variant="contained"
            disabled={confirmDisabled}
          >
            {busy ? '초기화 중…' : '초기화'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TerritoryReset;
